import {
  ClickAwayListener,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  Paper,
  Popper,
  Tooltip,
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@material-ui/icons/";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { MdOutlineLogout } from "react-icons/md";
import { useHistory } from "react-router-dom";
import logoImg from "../../img/logo.png";
import { isSuperAdminLogin, removeAuthObjects } from "../../utils";
import SidebarItems from "./SidebarItems";
import SIDEBAR_COLORS from "./sidebarTheme";

const drawerWidth = 195;
const collapsedWidth = 56;
const nestedMenuWidth = 205;
const DRAWER_TRANSITION_MS = 225;

const styles = (theme) => ({
  drawerPaper: {
    position: "fixed",
    top: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    height: "100vh",
    overflow: "hidden",
    borderRight: "none",
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    background: SIDEBAR_COLORS.gradientBg,
    zIndex: theme.zIndex.drawer,
    display: "flex",
    flexDirection: "column",
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: collapsedWidth,
    [theme.breakpoints.up("sm")]: {
      width: collapsedWidth,
    },
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 8px",
    minHeight: "42px",
  },
  moduleTitle: {
    color: "rgba(255, 255, 255, 0.4)",
    fontSize: "0.55rem",
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    height: "14px",
    padding: "3px 8px 1px 8px",
    boxSizing: "content-box",
    transition: "padding 0.15s ease",
  },
  titleVisible: {
    padding: "3px 8px 1px 8px",
  },
  titleHidden: {
    justifyContent: "center",
    padding: "3px 0 1px 0",
  },
  titleAbbrev: {
    fontSize: "0.55rem",
    fontWeight: 600,
    color: "rgba(255, 255, 255, 0.35)",
    lineHeight: "14px",
    letterSpacing: "0.5px",
  },
  titleFullText: {
    whiteSpace: "nowrap",
    lineHeight: "14px",
  },
  moduleContainer: {
    marginBottom: "0px",
    position: "relative",
  },
  moduleHeader: {
    color: SIDEBAR_COLORS.textDefault,
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    backgroundColor: "transparent",
    margin: "1px 4px",
    padding: "2px 6px",
    borderRadius: "4px",
    minHeight: "24px",
    position: "relative",
    "&:hover": {
      backgroundColor: SIDEBAR_COLORS.hoverBg,
    },
  },
  moduleHeaderCollapsed: {
    justifyContent: "center",
    padding: "2px 0",
  },
  activeModule: {
    backgroundColor: SIDEBAR_COLORS.activeBg,
    color: "#ffffff",
    "&:hover": {
      backgroundColor: SIDEBAR_COLORS.activeHoverBg,
    },
    "& span": {
      fontWeight: 500,
    },
    "& svg": {
      opacity: 1,
      color: "#ffffff",
    }
  },
  moduleIcon: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: "#ffffff", // Forced white inheritance
    flexShrink: 0,
    overflow: "hidden",
  },
  moduleIconVisible: {
    opacity: 1,
    minWidth: "16px",
    maxWidth: "16px",
    height: "16px",
    transition: theme.transitions.create(["opacity", "min-width", "max-width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  moduleIconHidden: {
    opacity: 0,
    minWidth: "0px",
    maxWidth: "0px",
    height: "16px",
    transition: theme.transitions.create(["opacity", "min-width", "max-width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  moduleIconCollapsed: {
    minWidth: "100%",
  },
  moduleHeaderContent: {
    display: "flex",
    flex: 1,
    alignItems: "center",
    overflow: "hidden",
    transition: "opacity 0.2s ease, max-width 0.2s ease, margin-left 0.2s ease",
    whiteSpace: "nowrap",
  },
  contentVisible: {
    opacity: 1,
    maxWidth: "145px",
    marginLeft: "6px",
  },
  contentHidden: {
    opacity: 0,
    maxWidth: "0px",
    marginLeft: "0px",
  },
  moduleName: {
    fontSize: "0.7rem",
    fontWeight: 500,
    color: "inherit",
    letterSpacing: "0.1px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  arrowVisible: {
    opacity: 1,
    transform: "scale(1)",
  },
  arrowHidden: {
    opacity: 0,
    transform: "scale(0)",
    width: "0px",
  },
  menuItems: {
    padding: "1px 0",
    backgroundColor: "transparent",
    width: "100%",
    boxSizing: "border-box",
    overflow: "hidden",
  },
  compactMenu: {
    backgroundColor: SIDEBAR_COLORS.hoverBg,
    margin: "1px 4px",
    borderRadius: "4px",
    border: `1px solid ${SIDEBAR_COLORS.borderNeutral}`,
  },
  logoSection: {
    borderBottom: `1px solid ${SIDEBAR_COLORS.border}`,
    backgroundColor: SIDEBAR_COLORS.primaryDark,
    minHeight: "40px",
    display: "flex",
    alignItems: "center",
    boxSizing: "border-box",
  },
  logoSectionExpanded: {
    padding: "2px 4px 2px 8px",
    transition: theme.transitions.create(["padding"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  logoSectionCollapsed: {
    padding: "1px 4px",
    justifyContent: "center",
    transition: theme.transitions.create(["padding"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  logoRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  logoBrand: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
  },
  logoBrandCollapsed: {
    justifyContent: "center",
  },
  logoTitle: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#ffffff",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  logoTitleVisible: {
    opacity: 1,
    maxWidth: "120px",
    marginLeft: "6px",
    transition: theme.transitions.create(["opacity", "max-width", "margin-left"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  logoTitleHidden: {
    opacity: 0,
    maxWidth: "0px",
    marginLeft: "0px",
    transition: theme.transitions.create(["opacity", "max-width", "margin-left"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  drawerCloseButton: {
    marginLeft: "auto",
    overflow: "hidden",
  },
  closeButtonVisible: {
    opacity: 1,
    maxWidth: "32px",
    transition: theme.transitions.create(["opacity", "max-width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  closeButtonHidden: {
    opacity: 0,
    maxWidth: "0px",
    padding: "0px",
    margin: "0px",
    pointerEvents: "none",
    transition: theme.transitions.create(["opacity", "max-width", "padding", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  logoImage: {
    objectFit: "contain",
    flexShrink: 0,
  },
  logoImageExpanded: {
    width: "28px",
    height: "28px",
    transition: theme.transitions.create(["width", "height"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  logoImageCollapsed: {
    width: "38px",
    height: "38px",
    transition: theme.transitions.create(["width", "height"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  logoutButton: {
    padding: "3px 6px",
    margin: "1px auto 3px auto",
    width: "calc(100% - 8px)",
    borderRadius: "4px",
    backgroundColor: "transparent",
    border: `none`,
    transition: "all 0.2s ease",
    color: SIDEBAR_COLORS.iconDefault,
    display: "flex",
    alignItems: "center",
    "&:hover": {
      backgroundColor: SIDEBAR_COLORS.hoverBg,
      color: "#ffffff",
    },
  },
  logoutIcon: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: "#ffffff", // Forced White
    flexShrink: 0,
    overflow: "hidden",
  },
  logoutIconVisible: {
    opacity: 1,
    minWidth: "16px",
    maxWidth: "16px",
    transition: theme.transitions.create(["opacity", "min-width", "max-width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  logoutIconHidden: {
    opacity: 0,
    minWidth: "0px",
    maxWidth: "0px",
    transition: theme.transitions.create(["opacity", "min-width", "max-width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  sidebarDivider: {
    margin: "3px 8px",
    backgroundColor: "#2b517c",
    height: "1px",
  },
  nestedMenuPopper: {
    zIndex: theme.zIndex.tooltip + 1,
  },
  nestedMenuPaper: {
    width: nestedMenuWidth,
    maxHeight: "480px",
    backgroundColor: SIDEBAR_COLORS.primaryDark,
    boxShadow:
      "0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
    borderRadius: "0px",
    border: `1px solid ${SIDEBAR_COLORS.border}`,
    overflow: "hidden",
    transform: "none",
    boxSizing: "border-box",
  },
  nestedMenuHeader: {
    padding: "6px 10px",
    borderBottom: `1px solid ${SIDEBAR_COLORS.borderNeutral}`,
    backgroundColor: SIDEBAR_COLORS.hoverBg,
    fontSize: "11px",
    fontWeight: 500,
    color: "#ffffff",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  nestedMenuContent: {
    maxHeight: "430px",
    overflowY: "auto",
    overflowX: "hidden",
    padding: "3px 0",
    width: "100%",
    boxSizing: "border-box",
    "&::-webkit-scrollbar": {
      width: "4px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: SIDEBAR_COLORS.hoverBg,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: SIDEBAR_COLORS.scrollbarThumb,
      borderRadius: "2px",
    },
  },
  moduleArrow: {
    fontSize: "12px",
    color: "#ffffff", // Forced White
    opacity: 1,
    flexShrink: 0,
    marginLeft: "auto",
    transition: "opacity 0.2s ease, transform 0.2s ease",
  },
  contentContainer: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    padding: "1px 0",
    "&::-webkit-scrollbar": {
      width: "4px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: SIDEBAR_COLORS.hoverBg,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: SIDEBAR_COLORS.scrollbarThumb,
      borderRadius: "2px",
    },
  },
});

const Sidebar = ({
  classes,
  drawerOpen,
  setDrawerOpen,
  drawerClose,
  validRoutes,
  activeRoute,
  isMobile,
}) => {
  const history = useHistory();

  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [moduleAccess, setModuleAccess] = useState([]);
  const [userRole, setUserRole] = useState("user");
  const [organizationId, setOrganizationId] = useState(null);
  const [activeNestedModule, setActiveNestedModule] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isHoveringNestedMenu, setIsHoveringNestedMenu] = useState(false);
  const [isFullyCollapsed, setIsFullyCollapsed] = useState(!drawerOpen);
  const [subItemAnchorEl, setSubItemAnchorEl] = useState(null);
  const [activeSubItem, setActiveSubItem] = useState(null);
  const [isHoveringSubMenu, setIsHoveringSubMenu] = useState(false);
  const subItemTimeoutRef = useRef(null);
  const moduleRefs = useRef({});
  const collapseTimeoutRef = useRef(null);
  const collapseVisualTimeoutRef = useRef(null);

  const showExpandedContent = drawerOpen || !isFullyCollapsed;

  const getModuleAbbrev = (title) => {
    const abbrevMap = {
      "User Management": "UM",
      "Chartering": "CH",
      "Operations": "OP",
      "Accounting": "AC",
      "Fuel Supply": "FS",
      "Knowledge Partner": "KP",
      "Utilities": "UT",
    };
    return abbrevMap[title] || title.charAt(0);
  };

  const renderModuleTitle = (title) => {
    const abbrev = getModuleAbbrev(title);
    return (
      <div
        className={clsx(
          classes.moduleTitle,
          showExpandedContent ? classes.titleVisible : classes.titleHidden
        )}
      >
        {showExpandedContent ? (
          <span className={classes.titleFullText}>{title}</span>
        ) : (
          <span className={classes.titleAbbrev}>{abbrev}</span>
        )}
      </div>
    );
  };

  useEffect(() => {
    const authData = JSON.parse(localStorage.getItem("auth"));
    if (authData && authData.moduleAccess) {
      setModuleAccess(authData.moduleAccess);
      setUserRole(authData.role || "user");
      setOrganizationId(authData.organizationId || null);
    }
  }, []);

  useEffect(() => {
    if (!drawerOpen) {
      setActiveNestedModule(null);
      setAnchorEl(null);
      setActiveSubItem(null);
      setSubItemAnchorEl(null);
      setIsHoveringNestedMenu(false);
      setIsHoveringSubMenu(false);
    }
  }, [drawerOpen]);

  useEffect(() => {
    const params = new URLSearchParams(activeRoute.split("?")[1] || "");
    const subTab = params.get("subTab");
    if (activeNestedModule !== "vessel" || !activeRoute.includes("tab=addVessel") || !subTab || subTab === "1") {
      setActiveSubItem(null);
      setSubItemAnchorEl(null);
      setIsHoveringSubMenu(false);
    }
  }, [activeNestedModule, activeRoute]);

  const checkIsRouteSelected = (item, currentActiveRoute) => {
    if (!item || !currentActiveRoute) return false;

    if (item?.subItems?.length || item?.url?.includes("tab=addVessel")) {
      return currentActiveRoute.includes("tab=addVessel");
    }

    if (item?.url === "/vesselManager") {
      return currentActiveRoute.startsWith("/vesselManager") && !currentActiveRoute.includes("tab=addVessel");
    }

    if (item?.activeRoutes?.length) {
      return item.activeRoutes.some((route) => route === currentActiveRoute);
    }

    return currentActiveRoute === item?.url;
  };

  useEffect(() => {
    if (collapseVisualTimeoutRef.current) {
      clearTimeout(collapseVisualTimeoutRef.current);
      collapseVisualTimeoutRef.current = null;
    }

    if (drawerOpen) {
      setIsFullyCollapsed(false);
      return;
    }

    collapseVisualTimeoutRef.current = setTimeout(() => {
      setIsFullyCollapsed(true);
    }, DRAWER_TRANSITION_MS);

    return () => {
      if (collapseVisualTimeoutRef.current) {
        clearTimeout(collapseVisualTimeoutRef.current);
        collapseVisualTimeoutRef.current = null;
      }
    };
  }, [drawerOpen]);

  const handleDrawerClose = () => {
    drawerClose(false);
    setActiveNestedModule(null);
    setAnchorEl(null);
    setActiveSubItem(null);
    setSubItemAnchorEl(null);
    setIsHoveringNestedMenu(false);
    setIsHoveringSubMenu(false);
  };

  const onClickLogout = () => {
    removeAuthObjects();
    history.push("/");
  };

  useEffect(() => {
    if (
      validRoutes.some(
        (route) => route.url === "/" || route.url === "/supportinformation"
      )
    ) {
      setIsSuperAdmin(true);
    } else {
      setIsSuperAdmin(false);
    }
  }, [validRoutes]);

  const categorizedRoutes = {
    cargo: validRoutes.filter((route) =>
      ["/orders", "/cargo-analysis"].includes(route.url)
    ),
    vessel: validRoutes.filter((route) =>
      route.url.startsWith("/vesselManager") || route.url === "/positionReader"
    ),
    calculations: validRoutes.filter((route) =>
      [
        "/calculator",
        "/cargo-vessel-playground",
        "/cargovessel",
        "/cargo-cargo-matrix",
        "/vessel-vessel-matrix",
        "/all-estimation",
        "/estimation-analysis",
      ].includes(route.url)
    ),
    contracts: validRoutes.filter((route) =>
      route.url.startsWith("/contracts")
    ),
    utilities: validRoutes.filter((route) =>
      [
        "/market-curve-analysis",
        "/my-market-curve-analysis",
        "/smart-laytime",
        "/smart-sof",
        "/addressBook",
        "/port-information",
        "/distance-matrix",
        "/support",
        "/messagebox",
      ].includes(route.url)
    ),
  };

  const contractsRoute = validRoutes.find((route) => route.url === "/operations");
  const voyageListRoute = validRoutes.find((route) => route.url === "/voyage-list");
  const userListRoute = validRoutes.find((route) => route.url === "/user-list");
  const accountingRoute = validRoutes.find((route) => route.url === "/accounting");
  const fuelSupplyRoute = validRoutes.find((route) => route.url === "/fuel-supply");
  const knowledgePartnerRoute = validRoutes.find((route) => route.url === "/knowledge-partner");

  const isMenuActive = (key) => {
    return categorizedRoutes[key]?.some((route) => route.url === activeRoute);
  };

  const shouldShowModule = (moduleName) => {
    return isSuperAdmin || moduleAccess.includes(moduleName.toLowerCase());
  };

  const handleModuleClick = (moduleKey, event) => {
    if (!drawerOpen) return;

    const currentTarget = event.currentTarget;

    if (activeNestedModule === moduleKey) {
      setActiveNestedModule(null);
      setAnchorEl(null);
    } else {
      setActiveNestedModule(moduleKey);
      setAnchorEl(currentTarget);
    }
  };

  const handleModuleHover = (moduleKey, event) => {
    if (!drawerOpen || isMobile) return;

    const currentTarget = event.currentTarget;
    if (activeNestedModule !== moduleKey) {
      setActiveSubItem(null);
      setSubItemAnchorEl(null);
      setIsHoveringSubMenu(false);
    }
    setActiveNestedModule(moduleKey);
    setAnchorEl(currentTarget);
  };

  const handleClickAway = () => {
    setActiveNestedModule(null);
    setAnchorEl(null);
    setActiveSubItem(null);
    setSubItemAnchorEl(null);
    setIsHoveringNestedMenu(false);
    setIsHoveringSubMenu(false);
  };

  const renderModuleButton = (moduleKey, moduleName, routes) => {
    const isActive = isMenuActive(moduleKey);
    const isSelected = activeNestedModule === moduleKey;

    return (
      <div key={moduleKey} className={classes.moduleContainer}>
        <div
          ref={(el) => (moduleRefs.current[moduleKey] = el)}
          className={clsx(
            classes.moduleHeader,
            isFullyCollapsed && classes.moduleHeaderCollapsed,
            {
              [classes.activeModule]: isActive || isSelected,
            }
          )}
          onClick={(e) => handleModuleClick(moduleKey, e)}
          onMouseEnter={(e) => handleModuleHover(moduleKey, e)}
        >
          <div
            className={clsx(
              classes.moduleIcon,
              classes.moduleIconVisible,
              !drawerOpen && classes.moduleIconCollapsed
            )}
          >
            <img
              src={routes[0]?.blackImg}
              alt={moduleName}
              className="w-3.5 h-3.5"
              style={{
                // contrast(1000%) forces every semi-transparent pixel to bright solid white
                filter: "brightness(0) invert(1) contrast(1000%)",
              }}
            />
          </div>
          <div
            className={clsx(
              classes.moduleHeaderContent,
              showExpandedContent
                ? classes.contentVisible
                : classes.contentHidden
            )}
          >
            <span className={classes.moduleName}>{moduleName}</span>
          </div>
          <ChevronRightIcon
            className={clsx(
              classes.moduleArrow,
              showExpandedContent ? classes.arrowVisible : classes.arrowHidden
            )}
            style={{
              transform: isSelected && isMobile ? "rotate(90deg)" : "none",
              color: "#ffffff"
            }}
          />
        </div>
        {isMobile && (
          <Collapse in={isSelected} timeout="auto" unmountOnExit>
            <List component="div" disablePadding style={{ paddingLeft: "20px", paddingRight: "4px" }}>
              {routes.map((item, i) => (
                <SidebarItems
                  key={i}
                  item={item}
                  index={i}
                  handleDrawerClose={() => {
                    handleDrawerClose();
                  }}
                  drawerOpen={true}
                  selectedRoute={item?.activeRoutes?.includes(activeRoute)}
                  isNested={true}
                />
              ))}
            </List>
          </Collapse>
        )}
      </div>
    );
  };

  const renderSingleRouteButton = (route) => {
    if (!route) return null;

    const isActive =
      route.url === activeRoute ||
      (route.activeRoutes && route.activeRoutes.includes(activeRoute));

    return (
      <div
        key={route.url}
        className={classes.moduleContainer}
        onMouseEnter={(e) => handleModuleHover("moduleKey", e)}
      >
        <div
          className={clsx(
            classes.moduleHeader,
            isFullyCollapsed && classes.moduleHeaderCollapsed,
            {
              [classes.activeModule]: isActive,
            }
          )}
          onClick={() => {
            history.push(route.url);
            handleDrawerClose();
          }}
        >
          <div
            className={clsx(
              classes.moduleIcon,
              classes.moduleIconVisible,
              !drawerOpen && classes.moduleIconCollapsed
            )}
          >
            <img
              src={route.blackImg}
              alt={route.name}
              className="w-3.5 h-3.5"
              style={{
                filter: "brightness(0) invert(1) contrast(1000%)",
              }}
            />
          </div>
          <div
            className={clsx(
              classes.moduleHeaderContent,
              showExpandedContent
                ? classes.contentVisible
                : classes.contentHidden
            )}
          >
            <span className={classes.moduleName}>{route.label}</span>
          </div>
        </div>
      </div >
    );
  };

  const renderNestedMenu = () => {
    if (
      isMobile ||
      !activeNestedModule ||
      !categorizedRoutes[activeNestedModule] ||
      !drawerOpen ||
      !anchorEl
    ) {
      return null;
    }

    const routes = categorizedRoutes[activeNestedModule];
    const moduleNames = {
      cargo: "Cargo",
      vessel: "Vessel",
      calculations: "Calculations",
      contracts: "Contracts",
      utilities: "Utilities",
    };

    const handleItemMouseEnter = (event, item) => {
      if (subItemTimeoutRef.current) {
        clearTimeout(subItemTimeoutRef.current);
        subItemTimeoutRef.current = null;
      }
      if (item?.subItems?.length) {
        setActiveSubItem(item);
        setSubItemAnchorEl(event.currentTarget);
      } else {
        setActiveSubItem(null);
        setSubItemAnchorEl(null);
      }
    };

    const handleItemMouseLeave = () => {
      subItemTimeoutRef.current = setTimeout(() => {
        if (!isHoveringSubMenu) {
          setActiveSubItem(null);
          setSubItemAnchorEl(null);
        }
      }, 150);
    };

    const getPopperOffset = () => {
      return "-28, 7";
    };

    return (
      <>
        <Popper
          open={Boolean(activeNestedModule && anchorEl)}
          anchorEl={anchorEl}
          placement="right-start"
          className={classes.nestedMenuPopper}
          modifiers={{
            flip: {
              enabled: false,
            },
            offset: {
              enabled: true,
              offset: getPopperOffset(activeNestedModule),
            },
            preventOverflow: {
              enabled: true,
              boundariesElement: "viewport",
            },
          }}
        >
          <ClickAwayListener onClickAway={handleClickAway}>
            <Paper
              className={classes.nestedMenuPaper}
              elevation={8}
              data-nested-menu="true"
              onMouseEnter={() => {
                setIsHoveringNestedMenu(true);
                if (collapseTimeoutRef.current) {
                  clearTimeout(collapseTimeoutRef.current);
                  collapseTimeoutRef.current = null;
                }
                if (!drawerOpen) {
                  setDrawerOpen(true);
                }
              }}
              onMouseLeave={() => {
                setIsHoveringNestedMenu(false);
                collapseTimeoutRef.current = setTimeout(() => {
                  if (!isHoveringSubMenu) {
                    setDrawerOpen(false);
                  }
                }, 300);
              }}
            >
              <div className={classes.nestedMenuHeader}>
                {moduleNames[activeNestedModule]}
              </div>
              <div className={classes.nestedMenuContent}>
                <List
                  className={classes.menuItems}
                  disablePadding
                  style={{ width: "100%" }}
                >
                  {routes.map((item, i) => {
                    const hasSubItems = Boolean(item?.subItems?.length);
                    return (
                      <div
                        key={i}
                        ref={(el) => {
                          if (el && hasSubItems && activeRoute.includes("tab=addVessel")) {
                            const params = new URLSearchParams(activeRoute.split("?")[1] || "");
                            const subTab = params.get("subTab");
                            if (subTab && subTab !== "1") {
                              if (!subItemAnchorEl || activeSubItem !== item) {
                                setSubItemAnchorEl(el);
                                setActiveSubItem(item);
                              }
                            }
                          }
                        }}
                        onMouseEnter={(e) => handleItemMouseEnter(e, item)}
                        onMouseLeave={handleItemMouseLeave}
                      >
                        <SidebarItems
                          item={item}
                          index={i}
                          handleDrawerClose={() => {
                            handleDrawerClose();
                            setActiveNestedModule(null);
                            setAnchorEl(null);
                            setActiveSubItem(null);
                            setSubItemAnchorEl(null);
                          }}
                          drawerOpen={true}
                          selectedRoute={checkIsRouteSelected(item, activeRoute)}
                          isNested={true}
                          hasSubItems={hasSubItems}
                        />
                      </div>
                    );
                  })}
                </List>
              </div>
            </Paper>
          </ClickAwayListener>
        </Popper>

        {/* Cascading Sub-Menu Popper for items with subItems */}
        <Popper
          open={Boolean(activeSubItem && subItemAnchorEl && activeNestedModule)}
          anchorEl={subItemAnchorEl}
          placement="right-start"
          className={classes.nestedMenuPopper}
          style={{ zIndex: 1400 }}
          modifiers={{
            flip: {
              enabled: false,
            },
            offset: {
              enabled: true,
              offset: "-55px, 6px",
            },
            preventOverflow: {
              enabled: true,
              boundariesElement: "viewport",
            },
          }}
        >
          <Paper
            className={classes.nestedMenuPaper}
            style={{ transform: "none" }}
            elevation={8}
            onMouseEnter={() => {
              setIsHoveringSubMenu(true);
              setIsHoveringNestedMenu(true);
              if (collapseTimeoutRef.current) {
                clearTimeout(collapseTimeoutRef.current);
                collapseTimeoutRef.current = null;
              }
              if (subItemTimeoutRef.current) {
                clearTimeout(subItemTimeoutRef.current);
                subItemTimeoutRef.current = null;
              }
              if (!drawerOpen) {
                setDrawerOpen(true);
              }
            }}
            onMouseLeave={() => {
              setIsHoveringSubMenu(false);
              setIsHoveringNestedMenu(false);
              subItemTimeoutRef.current = setTimeout(() => {
                setActiveSubItem(null);
                setSubItemAnchorEl(null);
              }, 150);
              collapseTimeoutRef.current = setTimeout(() => {
                setDrawerOpen(false);
              }, 300);
            }}
          >
            <div className={classes.nestedMenuHeader}>
              {activeSubItem?.label}
            </div>
            <div className={classes.nestedMenuContent}>
              <List
                className={classes.menuItems}
                disablePadding
                style={{ width: "100%" }}
              >
                {activeSubItem?.subItems?.map((subItem, idx) => (
                  <SidebarItems
                    key={idx}
                    item={subItem}
                    index={idx}
                    handleDrawerClose={() => {
                      handleDrawerClose();
                      setActiveNestedModule(null);
                      setAnchorEl(null);
                      setActiveSubItem(null);
                      setSubItemAnchorEl(null);
                    }}
                    drawerOpen={true}
                    selectedRoute={activeRoute.includes(subItem.url)}
                    isNested={true}
                  />
                ))}
              </List>
            </div>
          </Paper>
        </Popper>
      </>
    );
  };

  return (
    <>
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        anchor="left"
        onClose={drawerClose}
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !drawerOpen && !isMobile && classes.drawerPaperClose
          ),
        }}
        open={drawerOpen}
        onMouseEnter={() => {
          if (isMobile) return;
          !drawerOpen && setDrawerOpen(true);
          if (collapseTimeoutRef.current) {
            clearTimeout(collapseTimeoutRef.current);
            collapseTimeoutRef.current = null;
          }
        }}
        onMouseLeave={(e) => {
          if (isMobile) return;
          if (drawerOpen && !isHoveringNestedMenu && !isHoveringSubMenu) {
            collapseTimeoutRef.current = setTimeout(() => {
              if (!isHoveringNestedMenu && !isHoveringSubMenu) {
                setDrawerOpen(false);
              }
            }, 300);
          }
        }}
        style={{
          width: isMobile ? drawerWidth : (drawerOpen ? drawerWidth : collapsedWidth),
          transition: "width 225ms cubic-bezier(0.4, 0, 0.6, 1)",
        }}
      >
        <div
          className={clsx(
            classes.logoSection,
            drawerOpen ? classes.logoSectionExpanded : classes.logoSectionCollapsed
          )}
        >
          <div className={classes.logoRow}>
            <div className={clsx(classes.logoBrand, !drawerOpen && classes.logoBrandCollapsed)}>
              <div className={`bg-white rounded-full ${drawerOpen ? "px-1 py-0" : "px-0.5 py-0"}`}>
                <img
                  src={logoImg}
                  alt="Smart Sea Freight"
                  className={clsx(
                    classes.logoImage,
                    drawerOpen ? classes.logoImageExpanded : classes.logoImageCollapsed
                  )}
                />
              </div>

              <span
                className={clsx(
                  classes.logoTitle,
                  drawerOpen ? classes.logoTitleVisible : classes.logoTitleHidden
                )}
              >
                Smart Sea Freight
              </span>
            </div>
            <IconButton
              onClick={handleDrawerClose}
              size="small"
              className={clsx(
                "hover:bg-sky-50 flex-shrink-0",
                classes.drawerCloseButton,
                drawerOpen ? classes.closeButtonVisible : classes.closeButtonHidden
              )}
            >
              {/* Replaced 'text-gray-500' with manual pure white style to prevent fading */}
              <ChevronLeftIcon fontSize="small" style={{ color: "#ffffff" }} />
            </IconButton>
          </div>
        </div>

        <div className={classes.contentContainer}>
          {userRole === "superboss" && userListRoute && (
            <>
              {renderModuleTitle("User Management")}
              {renderSingleRouteButton(userListRoute)}
              <div className={classes.sidebarDivider} />
            </>
          )}

          {isSuperAdmin ? (
            <div className={isSuperAdminLogin() ? "flex flex-col gap-1.5 py-1" : undefined}>
              {validRoutes
                .filter(
                  (route) =>
                    route.url === "/" ||
                    route.url === "/supportinformation" ||
                    route.url === "/organization" ||
                    route.url === "/userOrganization" ||
                    route.url === "/vesselInformation" ||
                    route.url === "/admin/knowledge-partners" ||
                    route.url === "/market-curve-analysis" ||
                    route.url === "/my-market-curve-analysis"
                )
                .map((route) => renderSingleRouteButton(route))}
            </div>
          ) : (
            <>
              {/* CHARTERING SECTION */}
              {shouldShowModule("chartering") && (
                <>
                  {renderModuleTitle("Chartering")}

                  {categorizedRoutes.cargo.length > 0 &&
                    renderModuleButton("cargo", "Cargo", categorizedRoutes.cargo)}

                  {categorizedRoutes.vessel.length > 0 &&
                    renderModuleButton("vessel", "Vessel", categorizedRoutes.vessel)}

                  {categorizedRoutes.calculations.length > 0 &&
                    renderModuleButton("calculations", "Calculations", categorizedRoutes.calculations)}

                  {categorizedRoutes.contracts.length > 0 &&
                    renderModuleButton("contracts", "Contracts", categorizedRoutes.contracts)}

                  <div className={classes.sidebarDivider} />
                </>
              )}

              {/* OPERATIONS SECTION */}
              {shouldShowModule("operations") && (
                <>
                  {renderModuleTitle("Operations")}
                  {renderSingleRouteButton(contractsRoute)}
                  {renderSingleRouteButton(voyageListRoute)}
                  <div className={classes.sidebarDivider} />
                </>
              )}

              {/* ACCOUNTING SECTION */}
              {shouldShowModule("accounts") && accountingRoute && (
                <>
                  {renderModuleTitle("Accounting")}
                  {renderSingleRouteButton(accountingRoute)}
                  <div className={classes.sidebarDivider} />
                </>
              )}

              {/* FUEL SUPPLY SECTION */}
              {shouldShowModule("fuelSupply") && fuelSupplyRoute && (
                <>
                  {renderModuleTitle("Fuel Supply")}
                  {renderSingleRouteButton(fuelSupplyRoute)}
                  <div className={classes.sidebarDivider} />
                </>
              )}

              {/* KNOWLEDGE PARTNER SECTION */}
              {shouldShowModule("knowledgepartner") && knowledgePartnerRoute && (
                <>
                  {renderModuleTitle("Knowledge Partner")}
                  {renderSingleRouteButton(knowledgePartnerRoute)}
                  <div className={classes.sidebarDivider} />
                </>
              )}

              {/* UTILITIES SECTION */}
              {categorizedRoutes.utilities.length > 0 && (
                <>
                  {renderModuleTitle("Utilities")}
                  {renderModuleButton("utilities", "Utilities", categorizedRoutes.utilities)}
                  <div className={classes.sidebarDivider} />
                </>
              )}
            </>
          )}
        </div>

        <div className="mt-auto border-t border-t-[#2b517c]">
          <Tooltip title={drawerOpen ? "" : "Logout"} arrow placement="right">
            <ListItem
              button
              className={clsx(classes.logoutButton)}
              onClick={onClickLogout}
              style={{ justifyContent: showExpandedContent ? "flex-start" : "center" }}
            >
              <div
                className={clsx(
                  classes.logoutIcon,
                  classes.logoutIconVisible
                )}
              >
                <MdOutlineLogout
                  className="flex-shrink-0"
                  style={{ minWidth: "18px", height: "18px", color: "#ffffff" }}
                />
              </div>
              <span
                className={clsx(
                  "text-xs font-medium transition-all duration-200 overflow-hidden whitespace-nowrap",
                  showExpandedContent
                    ? "opacity-100 ml-2 max-w-[100px]"
                    : "opacity-0 ml-0 max-w-0"
                )}
              >
                Logout
              </span>
            </ListItem>
          </Tooltip>
        </div>
      </Drawer>

      {renderNestedMenu()}
    </>
  );
};

export default withStyles(styles, { withTheme: true })(Sidebar);
